import { Box } from '@mui/material';
import { ReactElement, ReactNode } from 'react';
import { tokens } from '@synergy/theme-palo';

const c = tokens.color;

type SegmentValue = string | number | boolean;

export interface PaloSegmentedOption {
    value: SegmentValue;
    label: ReactNode;
}

export interface PaloSegmentedToggleProps {
    value: SegmentValue;
    options: PaloSegmentedOption[];
    onChange: (value: SegmentValue) => void;
    disabled?: boolean;
    /** Stretch the control to fill its container and split segments evenly. */
    fullWidth?: boolean;
    /** For a heading line, where the full height would push the line apart. */
    size?: 'small';
}

/**
 * Dense segmented control per the PALO prototype: a bordered track with a filled
 * accent segment for the active option. Replaces the legacy pill-shaped dual
 * button so binary choices align with the surrounding dense form fields.
 */
export const PaloSegmentedToggle = ({
    value,
    options,
    onChange,
    disabled,
    fullWidth,
    size,
}: PaloSegmentedToggleProps): ReactElement => (
    <Box
        role="tablist"
        sx={{
            display: fullWidth ? 'flex' : 'inline-flex',
            width: fullWidth ? '100%' : 'auto',
            padding: 'small' === size ? '1px' : '3px',
            gap: 'small' === size ? '1px' : '3px',
            border: `1px solid ${c.lineHard}`,
            borderRadius: tokens.radius.default + 'px',
            // A locked control is drawn the way a locked field is: the same
            // ground, the same line. Dimming the whole track left the chosen
            // segment in the accent colour, which on a cancelled invoice read
            // as a switch still waiting to be thrown (#2231 R-125).
            backgroundColor: disabled ? c.canvas : c.surfaceAlt,
            borderColor: disabled ? c.line : c.lineHard,
        }}
    >
        {options.map(option => {
            const selected = option.value === value;

            return (
                <Box
                    key={String(option.value)}
                    role="tab"
                    aria-selected={selected}
                    onClick={() => !disabled && !selected && onChange(option.value)}
                    sx={{
                        flex: fullWidth ? 1 : 'none',
                        textAlign: 'center',
                        whiteSpace: 'nowrap',
                        padding: 'small' === size ? '1px 8px' : '5px 16px',
                        fontSize: 'small' === size ? '11px' : '12.5px',
                        fontWeight: 560,
                        lineHeight: 1.4,
                        borderRadius: tokens.radius.small + 'px',
                        cursor: disabled ? 'default' : 'pointer',
                        userSelect: 'none',
                        // Which option holds stays readable while locked —
                        // the segment keeps its fill, in the grey of a locked
                        // field rather than the accent.
                        color: disabled ? (selected ? c.ink2 : c.ink3) : selected ? c.inkInvert : c.ink2,
                        // A setting, not an action: the chosen side is drawn in
                        // the dark of the navigation, not the accent (#2290).
                        backgroundColor: selected ? (disabled ? c.line : c.chrome) : 'transparent',
                        transition: 'background-color .12s, color .12s',
                        '&:hover': disabled || selected ? undefined : { backgroundColor: c.surface, color: c.ink },
                    }}
                >
                    {option.label}
                </Box>
            );
        })}
    </Box>
);
